import { base32, safeEqual } from './encoding';
import { assertId, deriveKioskSecret, findKey, hmac, type BusinessKey, type KeySet } from './keys';
import { DEFAULT_WINDOW_SECONDS, windowIndex } from './member-code';

const MAC_BYTES = 10;

async function mac(secret: string, branchId: string, kioskId: string, win: number): Promise<string> {
  return base32(await hmac(secret, `K1|${branchId}|${kioskId}|${win}`, MAC_BYTES));
}

/**
 * Mode 2, kiosk wall QR: `K1.<kid>.<branchId>.<kioskId>.<window>.<mac>`.
 * The kiosk holds only its own derived secret (not the business key), so a stolen wall tablet
 * can only mimic itself.
 */
export async function generateKioskCode(
  kid: string, kioskSecret: string, branchId: string, kioskId: string, nowMs: number, windowSeconds = DEFAULT_WINDOW_SECONDS,
): Promise<string> {
  assertId('branchId', branchId);
  assertId('kioskId', kioskId);
  const w = windowIndex(nowMs, windowSeconds);
  return `K1.${kid}.${branchId}.${kioskId}.${w}.${await mac(kioskSecret, branchId, kioskId, w)}`;
}

/** Server issues this to a kiosk at pairing. */
export function issueKioskSecret(key: BusinessKey, branchId: string, kioskId: string): Promise<string> {
  return deriveKioskSecret(key, branchId, kioskId);
}

export type KioskVerifyResult =
  | { ok: true; branchId: string; kioskId: string; windowIndex: number }
  | { ok: false; reason: 'malformed' | 'unknown_key' | 'bad_signature' | 'stale' | 'future' };

export interface KioskVerifyOptions {
  windowSeconds?: number;
  /** How many windows back are fresh. Default 1 (current or previous). */
  maxAgeWindows?: number;
  /** How many windows ahead are tolerated (kiosk clock fast). Default 0. */
  futureWindows?: number;
}

/** Server-side: verify a scanned wall code is genuine and fresh. */
export async function verifyKioskCode(code: string, keys: KeySet, nowMs: number, opts: KioskVerifyOptions = {}): Promise<KioskVerifyResult> {
  const windowSeconds = opts.windowSeconds ?? DEFAULT_WINDOW_SECONDS;
  if (typeof code !== 'string') return { ok: false, reason: 'malformed' };
  const parts = code.split('.');
  if (parts.length !== 6 || parts[0] !== 'K1') return { ok: false, reason: 'malformed' };
  const [, kid, branchId, kioskId, winStr, given] = parts as [string, string, string, string, string, string];
  // Canonical digits only: the MAC covers the number, so "0123" must not pass as a second spelling of "123".
  if (!/^(0|[1-9]\d{0,11})$/.test(winStr) || !kid || !branchId || !kioskId || !given) return { ok: false, reason: 'malformed' };
  const win = Number(winStr);
  const key = findKey(keys, kid, nowMs);
  if (!key) return { ok: false, reason: 'unknown_key' };
  let secret: string;
  try {
    secret = await deriveKioskSecret(key, branchId, kioskId);
  } catch {
    return { ok: false, reason: 'malformed' };
  }
  if (!safeEqual(await mac(secret, branchId, kioskId, win), given)) return { ok: false, reason: 'bad_signature' };
  const now = windowIndex(nowMs, windowSeconds);
  if (win > now + (opts.futureWindows ?? 0)) return { ok: false, reason: 'future' };
  if (now - win > (opts.maxAgeWindows ?? 1)) return { ok: false, reason: 'stale' };
  return { ok: true, branchId, kioskId, windowIndex: win };
}
